import * as React from 'react'

import { Formik, FormikValues } from 'formik'
import { FiChevronRight } from 'react-icons/fi'
import Swal from 'sweetalert2'
import Loader from 'react-loader-spinner'
import TextareaAutosize from 'react-textarea-autosize'

import styled from '@theme/styled-components'
import Theme from '@theme/Theme'
import useI18n from '@store/i18n/useI18n'
import apis from '@app/api/api'
import useReducer from '@store/useReducer'
import * as AccountStore from '@store/account/account'

import Button from '@components/button/button'
import Picker from '@components/select/select'
import BusinessSelectModal from '@components/select/businessSelectModal'

import rights from '@utils/RightManager'
import useDebounce from '@utils/useDebounce'
import Logger from '@utils/Logger'

interface Props {
  navigation: Navigation
  screenStatus: ScreenStatus
  declaration?: BusinessDeclaration
  setDeclaration: (d: BusinessDeclaration) => void
  business?: BusinessDetails
  businessDetails?: AllBusinessDetails
  steps: BusinessStep[]
  regionList: Option[]
}

const DeclarationDetailsMenu = ({
  navigation,
  screenStatus,
  declaration,
  setDeclaration,
  business,
  businessDetails,
  steps,
  regionList,
}: Props) => {
  const i18n = useI18n()
  const account = useReducer(AccountStore.store, (s) => s.account)

  const [editing, setEditing] = React.useState(false)
  const [selectOpen, setSelectOpen] = React.useState(false)
  const [txt, bounced, setText] = useDebounce('')
  const [businesses, setBusinesses] = React.useState<Option[]>([])
  const [selectedRegion, setSelectedRegion] = React.useState<Option>()
  const [modalStatus, setModalStatus] = React.useState<ScreenStatus>('fetched')

  React.useEffect(() => {
    setSelectedRegion(regionList[0])
  }, [regionList])

  React.useEffect(() => {
    if (!!selectedRegion) {
      fetchBusinesses(bounced, selectedRegion.value)
    }
  }, [bounced, selectedRegion])

  const fetchBusinesses = (text: string, region: string) => {
    setModalStatus('loading')
    apis.admin.list
      .businesses({ text, region })
      .then((res) => {
        setBusinesses(res.options)
        setModalStatus('fetched')
      })
      .catch((err) => {
        setModalStatus('error')
        Logger.error('Une erreur est survenue lors de la récupération des affaires :', err)
      })
  }

  const declarationStep: BusinessStep | undefined = React.useMemo(
    () => declaration && steps.find((s) => s.id === declaration.description.idStep),
    [declaration, steps]
  )

  const declarationTheme: BusinessTheme | undefined = React.useMemo(
    () =>
      declaration && declarationStep && declarationStep.themes.find((t) => t.id === declaration.description.idTheme),
    [declaration, declarationStep]
  )

  const displayButton = React.useMemo((): 'none' | 'edit' | 'release' => {
    if (!declaration) {
      return 'none'
    }
    if (declaration.breakpoint && !declaration.breakpoint.release && rights.canReleaseBreakpoint()) {
      return 'release'
    }
    if (!declaration.breakpoint && rights.canEditDeclarationTexts()) {
      return 'edit'
    }
    return 'none'
  }, [declaration])

  const releaseBreakpoint = (declarationId: string, d: BusinessDeclaration) => {
    Swal.fire({
      icon: 'warning',
      title: i18n.t('components.declarationDetails.releaseBreakpoint.title'),
      text: i18n.t('components.declarationDetails.releaseBreakpoint.text'),
      showCancelButton: true,
      confirmButtonText: i18n.t('components.declarationDetails.releaseBreakpoint.confirm'),
      confirmButtonColor: 'green',
      cancelButtonText: i18n.t('components.declarationDetails.releaseBreakpoint.cancel'),
      cancelButtonColor: 'red',
    }).then((result) => {
      if (result.isConfirmed) {
        apis.declaration
          .releaseBreakpoint(declarationId)
          .then(() => {
            setDeclaration({
              ...d,
              breakpoint: d.breakpoint && {
                ...d.breakpoint,
                release: {
                  username: account ? account.firstname + ' ' + account.lastname : 'Utilisateur inconnu',
                  date: new Date().toString(),
                },
              },
            })
            Swal.fire({
              icon: 'success',
              title: i18n.t('components.declarationDetails.releaseBreakpoint.success.title'),
              text: i18n.t('components.declarationDetails.releaseBreakpoint.success.text'),
              showCancelButton: false,
              confirmButtonText: i18n.t('components.declarationDetails.releaseBreakpoint.success.confirm'),
              confirmButtonColor: 'green',
            })
          })
          .catch((err) => {
            Swal.fire({
              icon: 'error',
              title: i18n.t('components.declarationDetails.releaseBreakpoint.failure.title'),
              text: i18n.t('components.declarationDetails.releaseBreakpoint.failure.text'),
              showCancelButton: false,
              confirmButtonText: i18n.t('components.declarationDetails.releaseBreakpoint.failure.confirm'),
              confirmButtonColor: 'green',
            })
          })
      }
    })
  }

  const renderReadMode = (b: AllBusinessDetails, d: BusinessDeclaration) => (
    <>
      <Infos>
        {i18n.t('components.declarationDetails.info', {
          date: new Date(d.description.date),
          business: b.title,
          user: d.description.firstname + ' ' + d.description.lastname,
          company: d.description.company || 'GRDF',
        })}
      </Infos>
      {!!declarationStep && !!declarationTheme && (
        <>
          <Separator />
          <Categorisation>
            <StepThemeLibelle>{declarationStep.wording}</StepThemeLibelle>
            <IconContainer>
              <FiChevronRight size={15} />
            </IconContainer>
            <StepThemeLibelle themeColor>{declarationTheme.wording}</StepThemeLibelle>
          </Categorisation>
        </>
      )}
      {d.breakpoint && (
        <>
          <Separator />
          <InfosContainer>
            <InfosTitle>{i18n.t('components.declarationDetails.breakpoint')}</InfosTitle>
            <Infos>
              {d.breakpoint.release
                ? i18n.t('components.declarationDetails.releasedBreakpoint', {
                    date: new Date(d.breakpoint.release.date),
                    username: d.breakpoint.release.username,
                  })
                : i18n.t('components.declarationDetails.inprogressBreakpoint', {
                    date: new Date(d.breakpoint.creation.date),
                    username: d.breakpoint.creation.username,
                  })}
            </Infos>
          </InfosContainer>
        </>
      )}
      {!!d.description.comment && (
        <>
          <Separator />
          <InfosContainer>
            <InfosTitle>{i18n.t('components.declarationDetails.comment')}</InfosTitle>
            <Infos>{d.description.comment}</Infos>
          </InfosContainer>
        </>
      )}
      {!!d.description.location && (
        <>
          <Separator />
          <InfosContainer>
            <InfosTitle>{i18n.t('components.declarationDetails.location')}</InfosTitle>
            <Infos>{d.description.location}</Infos>
          </InfosContainer>
        </>
      )}
      {displayButton !== 'none' && declaration && (
        <BottomContainer>
          <ButtonContainer>
            {displayButton === 'release' ? (
              <Button
                type="navigation"
                title={i18n.t(`components.declarationDetails.buttons.releaseBreakpoint`)}
                onClick={() => releaseBreakpoint(d.id, declaration)}
                status="active"
              />
            ) : (
              <Button
                type="action"
                title={i18n.t(`components.declarationDetails.buttons.edit`)}
                onClick={() => setEditing(true)}
                status="active"
              />
            )}
          </ButtonContainer>
        </BottomContainer>
      )}
    </>
  )

  const editDeclaration = (values: FormikValues, actions: any) => {
    apis.declaration
      .edit(values as Declaration)
      .then((editedDeclaration) => {
        actions.setSubmitting(false)
        setEditing(false)
        setDeclaration(editedDeclaration as BusinessDeclaration)
        Swal.fire({
          title: i18n.t('components.declarationDetails.editionSuccess.title'),
          text: i18n.t('components.declarationDetails.editionSuccess.text'),
          icon: 'success',
        })
      })
      .catch(() => {
        actions.setSubmitting(false)
        Swal.fire({
          title: i18n.t('components.declarationDetails.editionFailure.title'),
          text: i18n.t('components.declarationDetails.editionFailure.text'),
          icon: 'question',
          showCancelButton: false,
          confirmButtonColor: '#3A8641',
          confirmButtonText: i18n.t('components.declarationDetails.editionFailure.confirm'),
        })
      })
  }

  const renderEditMode = (b: AllBusinessDetails, d: BusinessDeclaration) => (
    <FieldCategory initialValues={d as Declaration} onSubmit={editDeclaration}>
      {({ values, setFieldValue, submitForm, isSubmitting }) => {
        const stepOptions: Option[] = React.useMemo(
          () => steps.map((s) => ({ value: s.id, label: s.wording })),
          [steps]
        )
        const stepSelected: Option | undefined = React.useMemo(
          () => stepOptions.find((s) => s.value === values.description.idStep),
          [stepOptions, values.description.idStep]
        )
        const themeOptions: Option[] = React.useMemo(() => {
          const selectedStep = stepSelected && steps.find((s) => s.id === stepSelected.value)
          return selectedStep ? selectedStep.themes.map((t) => ({ value: t.id, label: t.wording })) : []
        }, [steps, stepSelected])
        const themeSelected: Option | undefined = React.useMemo(
          () => themeOptions.find((t) => t.value === values.description.idTheme),
          [themeOptions, values.description.idTheme]
        )

        const [declarationLabel, setDeclarationLabel] = React.useState(b.title)
        const declarationBusiness = React.useMemo(() => {
          return businesses.filter((business) => business.value === values.location.idBusiness)
        }, [businesses, values.location.idBusiness])

        return (
          <>
            <FieldContainer>
              <AsteriskContainer>
                <Asterisk>{i18n.t('components.declarationDetails.editMode.requiredField')}</Asterisk>
                <TitleField>{i18n.t('components.declarationDetails.editMode.dateField')}</TitleField>
              </AsteriskContainer>
              <NonEditableContainer>
                <NonEditableField>
                  {i18n.t('components.declarationDetails.editMode.dateBusiness', {
                    date: new Date(values.description.date),
                  })}
                </NonEditableField>
              </NonEditableContainer>
            </FieldContainer>
            <FieldContainer>
              <AsteriskContainer>
                <Asterisk>{i18n.t('components.declarationDetails.editMode.requiredField')}</Asterisk>
                <TitleField>{i18n.t('components.declarationDetails.editMode.businessField')}</TitleField>
              </AsteriskContainer>
              {rights.canReassignDeclarations() ? (
                <SelectorContainer>
                  <Field>{declarationLabel}</Field>
                  <EditButtonContainer>
                    <Button
                      title={i18n.t('common.edit')}
                      onClick={() => setSelectOpen(true)}
                      type="classic"
                      status="active"
                      size="small"
                    />
                  </EditButtonContainer>
                  {selectOpen && !!selectedRegion && (
                    <BusinessSelectModal
                      placeholder={i18n.t('components.detailsOrphan.placeholder.business')}
                      setSelectOpen={setSelectOpen}
                      regionList={regionList}
                      selectedRegion={selectedRegion}
                      setSelectedRegion={setSelectedRegion}
                      txt={txt}
                      setText={setText}
                      screenStatus={modalStatus}
                      businesses={businesses}
                      selected={declarationBusiness}
                      setDeclarationLabel={setDeclarationLabel}
                      setSelected={(selected) => setFieldValue('location.idBusiness', selected[0].value)}
                      singleSelect
                    />
                  )}
                </SelectorContainer>
              ) : (
                <NonEditableContainer>
                  <NonEditableField>{b.title}</NonEditableField>
                </NonEditableContainer>
              )}
            </FieldContainer>
            <Separator />
            <FieldContainer>
              <AsteriskContainer>
                <TitleField>{i18n.t('components.declarationDetails.editMode.commentField')}</TitleField>
              </AsteriskContainer>
              <CommentContainer
                value={values.description.comment}
                cacheMeasurements
                onChange={(event) => setFieldValue('description.comment', event.target.value)}
                placeholder={i18n.t('components.declarationDetails.editMode.commentPlaceholder')}
              />
            </FieldContainer>
            <FieldContainer>
              <AsteriskContainer>
                <TitleField>{i18n.t('components.declarationDetails.editMode.indicationField')}</TitleField>
              </AsteriskContainer>
              <FieldInput
                value={values.description.location}
                onChange={(event) => setFieldValue('description.location', event.target.value)}
                placeholder={i18n.t('components.declarationDetails.editMode.indicationPlaceholder')}
              />
            </FieldContainer>
            <FieldContainer>
              <AsteriskContainer>
                <Asterisk>{i18n.t('components.declarationDetails.editMode.requiredField')}</Asterisk>
                <TitleField>{i18n.t('components.declarationDetails.editMode.stepField')}</TitleField>
              </AsteriskContainer>
              <Picker
                options={stepOptions}
                selected={stepSelected}
                setSelected={(opt) => {
                  setFieldValue('description.idStep', opt && opt.value)
                  setFieldValue('description.idTheme', null)
                }}
                placeholder={i18n.t('components.declarationDetails.editMode.stepPlaceholder')}
                noMargin
                error={!stepSelected ? i18n.t('components.declarationDetails.editMode.stepMissing') : undefined}
              />
            </FieldContainer>
            <FieldContainer>
              <AsteriskContainer>
                <Asterisk>{i18n.t('components.declarationDetails.editMode.requiredField')}</Asterisk>
                <TitleField>{i18n.t('components.declarationDetails.editMode.themeField')}</TitleField>
              </AsteriskContainer>
              <Picker
                options={themeOptions}
                selected={themeSelected}
                setSelected={(opt) => setFieldValue('description.idTheme', opt && opt.value)}
                placeholder={i18n.t('components.declarationDetails.editMode.themePlaceholder')}
                noMargin
                isDisabled={themeOptions.length === 0}
                error={!themeSelected ? i18n.t('components.declarationDetails.editMode.themeMissing') : undefined}
              />
            </FieldContainer>

            <BottomContainer>
              <ButtonContainer>
                <Button
                  type="navigation"
                  title={i18n.t(`components.declarationDetails.cancel`)}
                  onClick={() => setEditing(false)}
                  status="active"
                />
              </ButtonContainer>
              <ButtonContainer>
                <Button
                  type="action"
                  title={i18n.t(`components.declarationDetails.save`)}
                  onClick={submitForm}
                  status={isSubmitting ? 'loading' : !themeSelected ? 'disabled' : 'active'}
                />
              </ButtonContainer>
            </BottomContainer>
          </>
        )
      }}
    </FieldCategory>
  )

  return (
    <Container>
      <ScreenTitleContainer>
        <ScreenTitle>
          {screenStatus === 'loading' || !declaration
            ? i18n.t('components.declarationDetails.title.loading')
            : i18n.t(`components.declarationDetails.title.date`, { date: new Date(declaration.description.date) })}
        </ScreenTitle>
        <Back onClick={() => navigation.goBack()}>{i18n.t('components.menu.back')}</Back>
      </ScreenTitleContainer>
      {screenStatus === 'loading' ? (
        <StatusContainer>
          <Loader type="ThreeDots" color={Theme.colors.classicButtonLoader} height={30} />
        </StatusContainer>
      ) : screenStatus === 'error' || !declaration || !business || !businessDetails ? (
        <ErrorLibelle>{i18n.t('components.detailsChantier.error')}</ErrorLibelle>
      ) : (
        <DeclarationContainer>
          {!editing ? renderReadMode(businessDetails, declaration) : renderEditMode(businessDetails, declaration)}
        </DeclarationContainer>
      )}
    </Container>
  )
}
export default DeclarationDetailsMenu

const Container = styled('div')`
  flex: 1;
  display: flex;
  overflow: hidden;
  margin-bottom: 30px;
  flex-direction: column;
  overflow-y: scroll;
  width: 390px;
  padding: 20px;
`
const ButtonContainer = styled('div')`
  display: flex;
  align-items: center;
  height: 125px;
  flex-direction: column;
  justify-content: space-around;
  margin-bottom: 25px;
`
const EditButtonContainer = styled('div')`
  margin-top: 10px;
`
const BottomContainer = styled('div')`
  bottom: 0px;
  width: 390px;
  display: flex;
  height: 200px;
  position: fixed;
  align-items: center;
  flex-direction: column;
  justify-content: flex-end;
  background: linear-gradient(359.87deg, #ffffff 50%, #00000000 100%);
`
const InfosContainer = styled('div')`
  border-radius: 10px;
  padding: 10px 0;
`
const Infos = styled('div')`
  ${(props) => props.theme.fonts.buttonBig};
  color: ${(props) => props.theme.colors.titleColor};
`
const InfosTitle = styled('div')`
  ${(props) => props.theme.fonts.boldLabel};
  color: ${(props) => props.theme.colors.markerEnded};
  padding-bottom: 12px;
`
const Separator = styled('div')`
  border: 1px solid ${(props) => props.theme.colors.menuBorder};
  margin: 20px 0px;
`
const Categorisation = styled('div')`
  display: flex;
  flex-wrap: wrap;
  flex-direction: row;
  align-items: center;
`
const StepThemeLibelle = styled('div')<{ themeColor?: boolean }>`
  ${(props) => props.theme.fonts.selectLibelle};
  color: ${(props) => (!!props.themeColor ? props.theme.colors.primary_blue : props.theme.colors.titleColor)};
  text-decoration-line: underline;
  flex: none;
  order: 0;
  flex-grow: 0;
`
const IconContainer = styled('div')`
  padding: 0 10px;
`
const ErrorLibelle = styled('div')`
  ${(props) => props.theme.fonts.noItem};
  text-align: center;
  margin: 40px;
`
const StatusContainer = styled('div')`
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
`
const DeclarationContainer = styled('div')`
  margin-top: 20px;
  margin-bottom: 80px;
`
const FieldCategory = styled(Formik)`
  display: flex;
  flex: 1;
  flex-direction: column;
  margin-bottom: 18px;
`
const AsteriskContainer = styled('div')`
  display: flex;
  flex-direction: row;
  padding-bottom: 10px;
`
const Asterisk = styled('div')`
  ${(props) => props.theme.fonts.calendarLibelle01};
  padding-right: 4px;
  color: ${(props) => props.theme.colors.primary_blue};
`
const TitleField = styled('div')`
  ${(props) => props.theme.fonts.calendarLibelle01};
  color: ${(props) => props.theme.colors.libelleColor};
`
const FieldInput = styled('input')`
  border: none;
  outline: none;
  padding: 14px 22px;
  display: flex;
  flex: 1;
  border: 1px solid ${(props) => props.theme.colors.searchbarBorder};
  border-radius: 8px;
  ${(props) => props.theme.fonts.selectLibelle};
`
const Field = styled('div')`
  border: none;
  outline: none;
  padding: 14px 22px;
  display: flex;
  flex: 1;
  border: 1px solid ${(props) => props.theme.colors.searchbarBorder};
  border-radius: 8px;
  ${(props) => props.theme.fonts.selectLibelle};
`
const NonEditableContainer = styled('div')`
  padding: 14px 22px;
  border: 1px solid ${(props) => props.theme.colors.searchbarBorder};
  border-radius: 8px;
  cursor: not-allowed;
`
const NonEditableField = styled('div')`
  ${(props) => props.theme.fonts.calendarLibelle04};
  color: ${(props) => props.theme.colors.primary_blue};
`
const FieldContainer = styled('div')`
  margin-bottom: 18px;
  display: flex;
  flex: 1;
  flex-direction: column;
`
const CommentContainer = styled(TextareaAutosize)`
  resize: none;
  padding: 14px 20px;
  border-radius: 7px;
  ${(props) => props.theme.fonts.selectLibelle};
`
const Back = styled('div')`
  ${(props) => props.theme.fonts.backLibelle};
  cursor: pointer;
`
const ScreenTitle = styled('div')`
  ${(props) => props.theme.fonts.title};
`
const ScreenTitleContainer = styled('div')`
  border: 0px solid ${(props) => props.theme.colors.menuBorder};
  border-bottom-width: 1px;
  border-top-width: 1px;
  padding: 15px 0;

  display: flex;
  align-items: center;
  justify-content: space-between;
`
const SelectorContainer = styled('div')`
  display: flex;
  flex: 1;
  flex-direction: column;

  margin: 5px;
`
